/**
 * PlanPanel: plan lifecycle UI — create, open, validate, submit, share.
 *
 * Guests and accounts:
 *  - Guests draw a GUEST DRAFT: painting, undo/redo, and live stats all
 *    work, and the draft persists in this browser (lib/localDrafts.ts).
 *    Validate / submit / share ask for an account; on sign-in the draft is
 *    promoted to a real plan (create + upload assignments) and the action
 *    continues — no work is lost and nothing has to be redrawn.
 *  - Signed-in residents see their own plans (GET /v1/plans/mine), so a
 *    reload never strands a plan.
 *
 * Backend fallbacks kept from the parallel-build era:
 *  - POST /v1/plans 501/404 → local draft.
 *  - POST /v1/plans/{id}/validate 501/404 → "not available" message.
 *  - POST /v1/plans/{id}/submit 422 → the hard-failure list.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type TenantConfig } from '../../lib/api';
import {
  createPlan,
  createPlanFromTemplate,
  getPlan,
  isNotImplemented,
  listMyPlans,
  SubmitRejectedError,
  submitPlan,
  validatePlan,
  type PlanSummary,
  type PlanTemplate,
  type SubmitResult,
  type ValidationReport,
} from '../../lib/plans';
import { isLocalPlanId } from '../../lib/localDrafts';
import { usePaintingStore } from '../../stores/painting';
import { selectIsAuthenticated, useAuthStore } from '../../stores/auth';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { ShareDialog } from './ShareDialog';
import { PlanComments } from './PlanComments';
import { ExportMenu } from '../ExportMenu';
import { exportPlan } from '../../lib/exports';
import {
  AlertIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  PlusIcon,
  SendIcon,
  ShareIcon,
  TrashIcon,
} from '../icons';

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Your session expired — sign in again to continue.';
    if (error.status === 0) return 'Can’t reach the server. Check your connection and try again.';
    return error.message || 'The server rejected the request.';
  }
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}

function relativeTime(iso: string | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(iso).toLocaleDateString();
}

export function StatusBadge({ status, local }: { status: PlanSummary['status']; local?: boolean }): JSX.Element {
  if (local) {
    return (
      <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
        In this browser
      </span>
    );
  }
  const classes =
    status === 'submitted'
      ? 'bg-emerald-100 text-emerald-800'
      : status === 'archived'
        ? 'bg-stone-200 text-stone-600'
        : 'bg-tenant-tint text-tenant-strong';
  return (
    <span className={`shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${classes}`}>
      {status}
    </span>
  );
}

function ValidationSummary({ report }: { report: ValidationReport }): JSX.Element {
  return (
    <div
      role="status"
      className={`rounded-xl border px-3 py-2.5 text-sm ${
        report.valid ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
      }`}
    >
      <p className={`flex items-center gap-1.5 font-semibold ${report.valid ? 'text-emerald-800' : 'text-red-800'}`}>
        {report.valid ? <CheckCircleIcon size={16} /> : <AlertIcon size={16} />}
        {report.valid
          ? 'Ready to submit'
          : `${report.failures.length} issue${report.failures.length === 1 ? '' : 's'} to fix`}
      </p>
      {report.failures.length > 0 && (
        <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs text-red-800">
          {report.failures.map((failure, i) => (
            <li key={i}>{failure}</li>
          ))}
        </ul>
      )}
      {report.warnings.length > 0 && (
        <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs text-amber-800">
          {report.warnings.map((warning, i) => (
            <li key={i}>{warning}</li>
          ))}
        </ul>
      )}
      <p className="mt-1.5 text-[11px] text-stone-500">
        Contiguity and compactness for each district are shown in the statistics panel.
      </p>
    </div>
  );
}

/**
 * Inline "Delete this plan?" strip. Deleting is permanent: account plans
 * are removed on the server, browser drafts from this browser.
 */
function ConfirmDelete({
  local,
  onCancel,
  onConfirm,
}: {
  local: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div role="group" aria-label="Confirm delete" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2">
      <p className="text-xs font-semibold text-red-900">
        {local ? 'Delete this draft from this browser?' : 'Delete this plan?'}
      </p>
      <p className="mt-0.5 text-[11px] leading-snug text-red-800">
        This can’t be undone.{local ? '' : ' Its share link stops working; copies other people made stay.'}
      </p>
      {error && <p className="mt-1 text-[11px] text-red-700">{error}</p>}
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="inline-flex h-8 items-center rounded-lg border border-stone-300 bg-white px-3 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-60"
        >
          Keep
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setError(null);
            onConfirm().catch((e: unknown) => {
              setError(errorMessage(e));
              setBusy(false);
            });
          }}
          className="inline-flex h-8 items-center rounded-lg bg-red-600 px-3 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
        >
          {busy ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  );
}

export function PlanPanel({ tenant }: { tenant: TenantConfig }): JSX.Element {
  const planId = usePaintingStore((s) => s.planId);
  const planName = usePaintingStore((s) => s.planName);
  const planStatus = usePaintingStore((s) => s.planStatus);
  const isLocalDraft = usePaintingStore((s) => s.isLocalDraft);
  const knownPlans = usePaintingStore((s) => s.knownPlans);
  const assignedCount = usePaintingStore((s) => Object.keys(s.assignments).length);
  const loadPlan = usePaintingStore((s) => s.loadPlan);
  const loadLocalDraft = usePaintingStore((s) => s.loadLocalDraft);
  const registerKnownPlan = usePaintingStore((s) => s.registerKnownPlan);
  const unloadPlan = usePaintingStore((s) => s.unloadPlan);
  const setPlanStatus = usePaintingStore((s) => s.setPlanStatus);
  const promoteLocalDraft = usePaintingStore((s) => s.promoteLocalDraft);
  const removePlan = usePaintingStore((s) => s.removePlan);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const queryClient = useQueryClient();

  const [newName, setNewName] = useState('');
  const [newCount, setNewCount] = useState(tenant.districts.count);
  const [template, setTemplate] = useState<'blank' | 'current_districts' | 'shared_plan'>('blank');
  const [shareUrl, setShareUrl] = useState('');
  const [templateNotice, setTemplateNotice] = useState<{ planId: string; message: string } | null>(
    null,
  );
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  /** Plan whose delete confirmation is showing (list row or the open plan). */
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [promoting, setPromoting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [report, setReport] = useState<ValidationReport | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitFailures, setSubmitFailures] = useState<string[]>([]);
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const { requireAuth, signInModal } = useRequireAuth();

  // Reset per-plan UI state when switching plans. The template notice is
  // keyed by plan id (not cleared here) so it survives the planId change
  // that loading a fresh template causes; it only renders for its own plan.
  useEffect(() => {
    setReport(null);
    setActionError(null);
    setSubmitFailures([]);
    setSubmitResult(null);
    setShareOpen(false);
  }, [planId]);

  // Remember the open plan so a reload drops people back where they were.
  const lastPlanKey = `rd:last-plan:${tenant.slug}`;
  useEffect(() => {
    try {
      if (planId) window.localStorage.setItem(lastPlanKey, planId);
    } catch {
      // Storage unavailable — nothing to restore next time.
    }
  }, [planId, lastPlanKey]);
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || planId) return;
    restoredRef.current = true;
    let lastId: string | null = null;
    try {
      lastId = window.localStorage.getItem(lastPlanKey);
    } catch {
      return;
    }
    if (!lastId) return;
    // Child effects run before MapView's: scope the store to this tenant now
    // (idempotent) so the parent's tenant reset can't wipe the restored plan.
    usePaintingStore.getState().setTenant(tenant.slug, tenant);
    if (isLocalPlanId(lastId)) {
      const draft = usePaintingStore.getState().knownPlans.find((p) => p.id === lastId);
      if (draft) loadLocalDraft(draft, tenant);
    } else if (isAuthenticated) {
      getPlan(lastId, tenant.districts.count, tenant.districts.labels)
        .then((detail) => {
          if (!usePaintingStore.getState().planId) loadPlan(detail, tenant);
        })
        .catch(() => undefined);
    }
    // Restore once per mount, from whatever is known at mount time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const plansQuery = useQuery({
    queryKey: ['my-plans', tenant.slug, isAuthenticated],
    queryFn: listMyPlans,
    retry: false,
    staleTime: 30 * 1000,
    enabled: planId === null && isAuthenticated,
  });

  const backendPlans = plansQuery.data ?? [];
  const mergedPlans: PlanSummary[] = [
    ...knownPlans.filter((k) => isLocalPlanId(k.id) || !backendPlans.some((b) => b.id === k.id)),
    ...backendPlans,
  ].sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));

  async function deleteOne(id: string): Promise<void> {
    await removePlan(id);
    setConfirmDeleteId(null);
    try {
      if (window.localStorage.getItem(lastPlanKey) === id) window.localStorage.removeItem(lastPlanKey);
    } catch {
      // ignore
    }
    void queryClient.invalidateQueries({ queryKey: ['my-plans'] });
  }

  async function openPlan(summary: PlanSummary): Promise<void> {
    setOpeningId(summary.id);
    setOpenError(null);
    try {
      if (isLocalPlanId(summary.id)) {
        loadLocalDraft(summary, tenant);
      } else {
        const detail = await getPlan(summary.id, tenant.districts.count, tenant.districts.labels);
        loadPlan(detail, tenant);
      }
    } catch (error) {
      setOpenError(
        isNotImplemented(error) ? 'That plan could not be found.' : errorMessage(error),
      );
    } finally {
      setOpeningId(null);
    }
  }

  function startGuestDraft(name: string, districtCount: number): void {
    const draft: PlanSummary = {
      id: `local-${crypto.randomUUID()}`,
      name,
      district_count: districtCount,
      status: 'draft',
      updated_at: new Date().toISOString(),
    };
    registerKnownPlan(draft);
    loadLocalDraft(draft, tenant);
    setNewName('');
  }

  async function handleCreate(): Promise<void> {
    if (template !== 'blank') {
      const context =
        template === 'current_districts'
          ? 'Sign in to start your plan from the current adopted districts.'
          : 'Sign in to start your plan from a shared plan link.';
      requireAuth(() => void handleCreateFromTemplate(), context);
      return;
    }
    const name = newName.trim() || 'Untitled plan';
    const districtCount = Math.max(1, Math.floor(newCount) || tenant.districts.count);
    // Guests draw in this browser; the draft becomes a real plan on sign-in.
    if (!isAuthenticated) {
      startGuestDraft(name, districtCount);
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const summary = await createPlan(name, districtCount);
      registerKnownPlan(summary);
      await openPlan(summary);
      setNewName('');
      void queryClient.invalidateQueries({ queryKey: ['my-plans'] });
    } catch (error) {
      if (isNotImplemented(error) || (error instanceof ApiError && error.status === 401)) {
        startGuestDraft(name, districtCount);
      } else {
        setCreateError(errorMessage(error));
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleCreateFromTemplate(): Promise<void> {
    const name = newName.trim() || 'Untitled plan';
    const districtCount = Math.max(1, Math.floor(newCount) || tenant.districts.count);
    if (template === 'shared_plan' && shareUrl.trim().length === 0) {
      setCreateError('Paste a shared plan link to start from it.');
      return;
    }
    const chosenTemplate: PlanTemplate =
      template === 'shared_plan'
        ? { type: 'shared_plan', token: shareUrl.trim() }
        : { type: template };
    setCreating(true);
    setCreateError(null);
    setTemplateNotice(null);
    try {
      const detail = await createPlanFromTemplate(
        name,
        districtCount,
        chosenTemplate,
        tenant.districts.labels,
      );
      registerKnownPlan(detail);
      // Load the detail directly (it carries the template's assignments);
      // scope the tenant first so a slug change can't wipe the fresh plan.
      const painting = usePaintingStore.getState();
      painting.setTenant(tenant.slug, tenant);
      painting.loadPlan(detail, tenant);
      setNewName('');
      setShareUrl('');
      setTemplateNotice({
        planId: detail.id,
        message:
          template === 'current_districts'
            ? 'Started from the current adopted districts — adjust the boundaries, then validate and submit.'
            : 'Copied from the shared plan — adjust the boundaries, then validate and submit.',
      });
    } catch (error) {
      setCreateError(errorMessage(error));
    } finally {
      setCreating(false);
    }
  }

  /**
   * Run `action` against a real backend plan id. Guest drafts ask the user
   * to sign in, get promoted to an account plan, then the action proceeds.
   */
  function withAccountPlan(context: string, action: (id: string) => void | Promise<void>): void {
    if (!planId) return;
    if (!isLocalDraft) {
      requireAuth(() => void action(planId), context);
      return;
    }
    requireAuth(() => {
      setPromoting(true);
      setActionError(null);
      promoteLocalDraft(tenant)
        .then((id) => {
          void queryClient.invalidateQueries({ queryKey: ['my-plans'] });
          return action(id);
        })
        .catch((error: unknown) => setActionError(`Couldn’t save your draft to your account: ${errorMessage(error)}`))
        .finally(() => setPromoting(false));
    }, context);
  }

  async function handleValidate(id: string): Promise<void> {
    setValidating(true);
    setActionError(null);
    try {
      const result = await validatePlan(id);
      setReport(result);
      // Publish to the painting store so the district statistics panel can
      // merge contiguity + compactness into each row.
      usePaintingStore.getState().setValidationReport(result);
    } catch (error) {
      setActionError(
        isNotImplemented(error) ? 'Validation is not available on this server yet.' : errorMessage(error),
      );
    } finally {
      setValidating(false);
    }
  }

  async function handleSubmit(id: string): Promise<void> {
    setSubmitting(true);
    setActionError(null);
    setSubmitFailures([]);
    setSubmitResult(null);
    try {
      // Flush pending autosaves first so the frozen snapshot matches the map
      // and the latest comments travel with the submission.
      await usePaintingStore.getState().saveNow();
      await usePaintingStore.getState().saveComments();
      const result = await submitPlan(id);
      setSubmitResult(result);
      setPlanStatus('submitted');
      void queryClient.invalidateQueries({ queryKey: ['my-plans'] });
    } catch (error) {
      if (error instanceof SubmitRejectedError) {
        setSubmitFailures(error.failures);
      } else if (isNotImplemented(error)) {
        setActionError('Submission is not available on this server yet.');
      } else {
        setActionError(errorMessage(error));
      }
    } finally {
      setSubmitting(false);
    }
  }

  /* ── No plan open: create / open ─────────────────────────────── */
  if (!planId) {
    return (
      <div className="space-y-5">
        <div>
          <h2 className="text-base font-bold text-ink">Start a plan</h2>
          <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
            Paint census blocks into {tenant.districts.count} districts. Population totals update as
            you draw.
          </p>
        </div>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void handleCreate();
          }}
        >
          <label className="field-label">
            Plan name
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Draft A — community proposal"
              className="field-input"
            />
          </label>
          <label className="field-label">
            Number of districts
            <input
              type="number"
              min={1}
              max={50}
              value={newCount}
              onChange={(e) => setNewCount(Number(e.target.value))}
              className="field-input w-24"
            />
          </label>
          <fieldset>
            <legend className="field-label">Starting point</legend>
            <div className="mt-1.5 space-y-1.5">
              {(
                [
                  {
                    value: 'blank',
                    title: 'Blank map',
                    body: 'Start from zero and paint every district yourself.',
                    disabled: false,
                  },
                  {
                    value: 'current_districts',
                    title: 'Current districts',
                    body: tenant.has_adopted_districts
                      ? 'Start from the adopted map and adjust. Requires an account.'
                      : 'Not available — current districts haven’t been published in a usable format.',
                    disabled: !tenant.has_adopted_districts,
                  },
                  {
                    value: 'shared_plan',
                    title: 'Someone’s shared plan',
                    body: 'Copy a shared plan link into your drafts. Requires an account.',
                    disabled: false,
                  },
                ] as const
              ).map((opt) => {
                const checked = template === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-2.5 rounded-xl border px-3 py-2 transition-colors ${
                      opt.disabled
                        ? 'cursor-not-allowed border-stone-200 bg-stone-50 opacity-60'
                        : checked
                          ? 'cursor-pointer border-tenant-primary bg-tenant-soft'
                          : 'cursor-pointer border-stone-200 bg-white hover:border-stone-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="plan-template"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setTemplate(opt.value)}
                      disabled={opt.disabled}
                      className="mt-1 accent-[var(--tenant-primary)]"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-ink">{opt.title}</span>
                      <span className="block text-xs leading-snug text-stone-500">{opt.body}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            {template === 'shared_plan' && (
              <label className="field-label mt-2">
                Share link
                <input
                  type="url"
                  value={shareUrl}
                  onChange={(e) => setShareUrl(e.target.value)}
                  placeholder="https://…/p/abc123"
                  className="field-input"
                />
              </label>
            )}
          </fieldset>
          <button type="submit" disabled={creating} className="btn-primary w-full">
            <PlusIcon size={16} />
            {creating ? 'Creating…' : template === 'blank' ? 'Create plan' : 'Start from template'}
          </button>
          {createError && <p className="text-xs text-red-600">{createError}</p>}
          {!isAuthenticated && (
            <p className="text-[11px] leading-relaxed text-stone-500">
              No account needed to draw — your plan saves in this browser. Sign in later to submit
              or share it.
            </p>
          )}
        </form>

        <div>
          <h3 className="panel-title">Your plans</h3>
          {plansQuery.isLoading && <p className="mt-2 text-xs text-stone-500">Loading your plans…</p>}
          {plansQuery.error && (
            <p className="mt-2 text-xs text-red-600">{errorMessage(plansQuery.error)}</p>
          )}
          {mergedPlans.length === 0 && !plansQuery.isLoading && (
            <p className="mt-2 rounded-xl border border-dashed border-stone-300 px-3 py-3 text-xs text-stone-500">
              {isAuthenticated
                ? 'Plans you create will be listed here.'
                : 'Plans you draw will be listed here. Sign in to see plans saved to your account.'}
            </p>
          )}
          {mergedPlans.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {mergedPlans.map((plan) => {
                const local = isLocalPlanId(plan.id);
                // Submitted plans are part of the public record: no delete.
                const deletable = local || plan.status !== 'submitted';
                return (
                  <li key={plan.id} className="space-y-1.5">
                    <div className="group flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => void openPlan(plan)}
                        disabled={openingId !== null}
                        className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2 text-left transition-colors hover:border-tenant-primary/50 hover:bg-tenant-soft disabled:opacity-60"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-ink">{plan.name}</span>
                          <span className="mt-1 flex items-center gap-1.5 overflow-hidden whitespace-nowrap text-[11px] text-stone-500">
                            <StatusBadge status={plan.status} local={local} />
                            <span className="truncate">
                              {plan.district_count} districts
                              {plan.updated_at && ` · ${relativeTime(plan.updated_at)}`}
                            </span>
                          </span>
                        </span>
                        <span className="shrink-0 text-xs font-semibold text-tenant-primary">
                          {openingId === plan.id ? 'Opening…' : 'Open'}
                        </span>
                      </button>
                      {deletable ? (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(confirmDeleteId === plan.id ? null : plan.id)}
                          aria-expanded={confirmDeleteId === plan.id}
                          className="btn-ghost h-9 w-9 shrink-0 p-0 text-stone-400 hover:text-red-700"
                          aria-label={`Delete ${plan.name}`}
                          title={local ? 'Delete this draft' : 'Delete this plan'}
                        >
                          <TrashIcon size={16} />
                        </button>
                      ) : (
                        <span
                          className="h-9 w-9 shrink-0"
                          title="Submitted plans are part of the public record and can’t be deleted"
                        />
                      )}
                    </div>
                    {confirmDeleteId === plan.id && (
                      <ConfirmDelete
                        local={local}
                        onCancel={() => setConfirmDeleteId(null)}
                        onConfirm={() => deleteOne(plan.id)}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {openError && <p className="mt-1 text-xs text-red-600">{openError}</p>}
        </div>
        {signInModal}
      </div>
    );
  }

  /* ── Plan open ───────────────────────────────────────────────── */
  const submitted = planStatus === 'submitted';
  const busy = promoting || validating || submitting;

  return (
    <div className="space-y-4">
      <div>
        <button
          type="button"
          onClick={() => {
            try {
              window.localStorage.removeItem(lastPlanKey);
            } catch {
              // ignore
            }
            unloadPlan();
          }}
          className="-ml-1 inline-flex items-center gap-0.5 rounded px-1 text-xs font-semibold text-stone-500 hover:text-ink"
        >
          <ChevronLeftIcon size={14} /> All plans
        </button>
        <h2 className="mt-1 break-words text-base font-bold leading-snug text-ink">{planName}</h2>
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-stone-500">
          <StatusBadge status={planStatus} local={isLocalDraft} />
          <span>{assignedCount.toLocaleString('en-US')} blocks painted</span>
        </p>
      </div>

      {templateNotice && templateNotice.planId === planId && (
        <div
          role="status"
          className="flex items-start justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
        >
          <p>{templateNotice.message}</p>
          <button
            type="button"
            onClick={() => setTemplateNotice(null)}
            aria-label="Dismiss"
            className="shrink-0 rounded px-1 text-emerald-700 hover:bg-emerald-100"
          >
            ✕
          </button>
        </div>
      )}

      {isLocalDraft && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2.5">
          <p className="text-xs font-semibold text-amber-900">Saved in this browser only</p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-amber-800">
            {isAuthenticated
              ? 'Move this draft into your account to validate, share, or submit it.'
              : 'Create a free account (or sign in) to keep it safe and to validate, share, or submit it.'}
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => withAccountPlan('Sign in to save this plan to your account.', () => undefined)}
            className="btn-secondary btn-sm mt-2"
          >
            {promoting ? 'Saving to your account…' : 'Save to my account'}
          </button>
        </div>
      )}

      <PlanComments />

      <div className="space-y-2">
        <h3 className="panel-title">Share &amp; download</h3>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() =>
              withAccountPlan('Sign in to share this plan with a public link.', () => setShareOpen(true))
            }
            disabled={busy}
            title="Get a public link friends can view, co-sign, or copy"
            className="btn-secondary w-full justify-center"
          >
            <ShareIcon size={16} />
            Share link
          </button>
          <ExportMenu
            variant="secondary"
            block
            align="right"
            onExport={(format) => {
              const st = usePaintingStore.getState();
              return exportPlan(format, {
                name: st.planName,
                districtCount: st.districtCount,
                assignments: st.assignments,
                labels: st.labels,
                colors: st.colors,
                comments: st.comments,
              });
            }}
            unavailable={assignedCount === 0 ? { shapefile: 'Paint some blocks first' } : {}}
          />
        </div>
      </div>

      {/* Submitting freezes the plan, so it is the last step in the panel. */}
      <div className="space-y-2">
        <h3 className="panel-title">Check &amp; submit</h3>
        <button
          type="button"
          onClick={() =>
            withAccountPlan('Sign in to check this plan against the legal requirements.', (id) =>
              handleValidate(id),
            )
          }
          disabled={busy}
          title="Run contiguity, population balance, and compactness checks"
          className="btn-secondary w-full justify-start"
        >
          <CheckCircleIcon size={16} />
          {validating ? 'Checking…' : 'Check plan'}
          <span className="ml-auto text-[11px] font-normal text-stone-400">balance · contiguity</span>
        </button>
        {report && <ValidationSummary report={report} />}
        <button
          type="button"
          onClick={() =>
            withAccountPlan('Sign in to submit this plan to the jurisdiction.', (id) => handleSubmit(id))
          }
          disabled={busy || submitted}
          title="Freeze a canonical snapshot of this plan and send it for staff review"
          className="btn-primary w-full justify-start"
        >
          <SendIcon size={16} />
          {submitting ? 'Submitting…' : submitted ? 'Submitted' : 'Submit plan'}
        </button>
      </div>

      {signInModal}
      {shareOpen && planId && !isLocalDraft && (
        <ShareDialog planId={planId} planName={planName} onClose={() => setShareOpen(false)} />
      )}
      {actionError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">
          {actionError}
        </p>
      )}

      {submitFailures.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5" role="alert">
          <h3 className="text-xs font-bold text-red-800">Not submitted — fix these first</h3>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-red-800">
            {submitFailures.map((failure, i) => (
              <li key={i}>{failure}</li>
            ))}
          </ul>
        </div>
      )}
      {submitResult && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3" role="status">
          <p className="flex items-center gap-1.5 text-sm font-bold text-emerald-900">
            <CheckCircleIcon size={16} /> Plan submitted
          </p>
          <dl className="mt-2 space-y-1 text-xs text-emerald-800">
            {submitResult.submission_id && (
              <div className="flex gap-2">
                <dt className="shrink-0 font-semibold">Receipt</dt>
                <dd className="break-all font-mono">{submitResult.submission_id}</dd>
              </div>
            )}
            {submitResult.submitted_at && (
              <div className="flex gap-2">
                <dt className="shrink-0 font-semibold">Submitted</dt>
                <dd>{new Date(submitResult.submitted_at).toLocaleString()}</dd>
              </div>
            )}
            <div className="flex gap-2">
              <dt className="shrink-0 font-semibold">Snapshot</dt>
              <dd className="break-all font-mono">
                {submitResult.snapshot_hash
                  ? `sha256:${submitResult.snapshot_hash.slice(0, 16)}…`
                  : '(no hash returned)'}
              </dd>
            </div>
          </dl>
          <p className="mt-2 text-xs leading-relaxed text-emerald-700">
            Your plan is frozen and queued for staff review. It will be considered alongside other
            public submissions at the next hearing — nothing further is needed from you.
          </p>
        </div>
      )}
      {!submitted && planId && (
        <div className="border-t border-stone-100 pt-3">
          {confirmDeleteId === planId ? (
            <ConfirmDelete
              local={isLocalDraft}
              onCancel={() => setConfirmDeleteId(null)}
              onConfirm={() => deleteOne(planId)}
            />
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmDeleteId(planId)}
              className="inline-flex items-center gap-1.5 rounded px-1 text-xs font-semibold text-stone-500 hover:text-red-700 disabled:opacity-50"
            >
              <TrashIcon size={14} />
              {isLocalDraft ? 'Delete this draft' : 'Delete this plan'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
